import React from 'react';
import ProfileAssistantRow from '../../GeneralProfile/ProfileAssistantRow';
import ProfileUserAnswerRow from '../../GeneralProfile/ProfileUserAnswerRow';
import type { OtherIncomeEarnerFlow } from './useOtherIncomeEarnerFlow';
import type { OtherIncomeClaimEntry, OtherIncomeEarnerStep } from './otherIncomeEarnerTypes';
import { MEMBER_LOOP_STEPS } from './otherIncomeEarnerTypes';
import { isSkippedStep } from './otherIncomeEarnerSteps';

const MEMBER_DISPLAY_ORDER: OtherIncomeEarnerStep[] = [
  'pickMember',
  'earnerRole',
  'earnerRoleOther',
  'earnerName',
  'earnerAddress',
  'memberToEarnerRelationship',
  'changedDuringYear',
  'changeDate',
  'changeReason',
  'changeReasonOther',
];

function AnswerRow({
  flow,
  fieldStep,
  children,
  historyLocked,
}: {
  flow: OtherIncomeEarnerFlow;
  fieldStep: OtherIncomeEarnerStep;
  children: React.ReactNode;
  historyLocked: boolean;
}) {
  if (historyLocked) {
    return <ProfileUserAnswerRow>{children}</ProfileUserAnswerRow>;
  }
  return (
    <ProfileUserAnswerRow
      showUndo={flow.canShowUndoForStep(fieldStep)}
      onUndo={() => void flow.handleUndo(fieldStep)}
      undoLabel={flow.undoLabel}
    >
      {children}
    </ProfileUserAnswerRow>
  );
}

function MemberQA({
  flow,
  entry,
  historyLocked,
}: {
  flow: OtherIncomeEarnerFlow;
  entry?: OtherIncomeClaimEntry;
  historyLocked: boolean;
}) {
  const historical = entry != null;
  const context = entry ?? flow.draft;

  return (
    <>
      {MEMBER_DISPLAY_ORDER.map((s) => {
        if (isSkippedStep(s, context)) return null;
        if (!historical && !flow.isCommitted(s)) return null;
        const q =
          s === 'changedDuringYear'
            ? flow.changedDuringYearQuestion()
            : flow.t(flow.stepQuestionKey(s));
        const answer = flow.userAnswerForStep(s, entry);
        if (!answer) return null;
        return (
          <React.Fragment key={s}>
            <ProfileAssistantRow>{q}</ProfileAssistantRow>
            {historical && !flow.canShowUndoForStep(s) ? (
              <ProfileUserAnswerRow undoLabel={flow.undoLabel}>{answer}</ProfileUserAnswerRow>
            ) : (
              <AnswerRow flow={flow} fieldStep={s} historyLocked={historyLocked}>
                {answer}
              </AnswerRow>
            )}
          </React.Fragment>
        );
      })}
    </>
  );
}

function MemberBlock({
  flow,
  entry,
  historyLocked,
}: {
  flow: OtherIncomeEarnerFlow;
  entry: OtherIncomeClaimEntry;
  historyLocked: boolean;
}) {
  return <MemberQA flow={flow} entry={entry} historyLocked={historyLocked} />;
}

export default function OtherIncomeEarnerChatThread({
  flow,
  historyLocked = false,
}: {
  flow: OtherIncomeEarnerFlow;
  historyLocked?: boolean;
}) {
  const { t, snap, step } = flow;
  const inMemberFlow =
    snap.anyClaimedByOther === 'yes' &&
    !flow.reviewMode &&
    (MEMBER_LOOP_STEPS.includes(step) || step === 'addAnother');

  return (
    <>
      <ProfileAssistantRow>{t(flow.stepQuestionKey('intro'))}</ProfileAssistantRow>

      {flow.isCommitted('anyClaimedGate') && snap.anyClaimedByOther !== '' ? (
        <>
          <ProfileAssistantRow>{t(flow.stepQuestionKey('anyClaimedGate'))}</ProfileAssistantRow>
          <AnswerRow flow={flow} fieldStep="anyClaimedGate" historyLocked={historyLocked}>
            {flow.userAnswerForStep('anyClaimedGate')}
          </AnswerRow>
        </>
      ) : null}

      {snap.entries.map((entry) => (
        <MemberBlock key={entry.empFamilyId} flow={flow} entry={entry} historyLocked={historyLocked} />
      ))}

      {inMemberFlow && (flow.draft.empFamilyId > 0 || flow.isCommitted('pickMember')) ? (
        <MemberQA flow={flow} historyLocked={historyLocked} />
      ) : null}

      {flow.isCommitted('addAnother') && flow.userAnswerForStep('addAnother') ? (
        <>
          <ProfileAssistantRow>{t(flow.stepQuestionKey('addAnother'))}</ProfileAssistantRow>
          <AnswerRow flow={flow} fieldStep="addAnother" historyLocked={historyLocked}>
            {flow.userAnswerForStep('addAnother')}
          </AnswerRow>
        </>
      ) : null}

      {flow.reviewMode && step === 'review' && !historyLocked && (
        <ProfileAssistantRow>{t('submitDocuments.dependentDeduction.otherIncomeEarner.reviewSummary')}</ProfileAssistantRow>
      )}

      {!flow.reviewMode && step !== 'review' && (
        <>
          {step === 'anyClaimedGate' && !flow.isCommitted('anyClaimedGate') ? (
            <ProfileAssistantRow>{t(flow.stepQuestionKey('anyClaimedGate'))}</ProfileAssistantRow>
          ) : null}

          {MEMBER_LOOP_STEPS.includes(step) && !flow.isCommitted(step) ? (
            <ProfileAssistantRow>
              {step === 'changedDuringYear'
                ? flow.changedDuringYearQuestion()
                : t(flow.stepQuestionKey(step))}
            </ProfileAssistantRow>
          ) : null}

          {step === 'addAnother' && !flow.isCommitted('addAnother') ? (
            <ProfileAssistantRow>{t(flow.stepQuestionKey('addAnother'))}</ProfileAssistantRow>
          ) : null}
        </>
      )}
    </>
  );
}
